import { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableRow, Paper, Checkbox, TableCell, Theme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Loading } from '../../../Loading';
import { useLocalStorage } from '../../../../hooks';
import { useChildren } from '../../hooks';
import { RowProvider } from '../../providers';
import { Row, RowType } from '../Row';
import { Cell, CellType } from '../Cell';
import { EmptyData, EmptyDataType } from '../EmptyData';
import { SelectButton, SelectButtonType } from '../SelectButton';
import { TableHeadRoot, TableToolbar, TableToolbarSelectRow } from './components';

const useStylesTable = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      backgroundColor: theme.palette.background.default,
    },
    paper: {
      width: '100%',
      overflowX: 'auto',
      marginBottom: theme.spacing(1),
    },
    headerCell: {
      borderRight: `1px ${theme.palette.grey.A400} solid`,
      borderBottom: `1px ${theme.palette.grey.A400} solid`,
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      paddingLeft: theme.spacing(2),
    },
    table: {
      border: `1px ${theme.palette.grey.A400} solid`,
    },
  })
);

const useStyles = makeStyles({
  root: {
    width: '100%',
    overflowX: 'visible',
  },
  tableHoC: {
    overflowY: 'hidden',
    overflowX: 'auto',
    position: 'relative',
  },
  tableLayoutFixed: {
    tableLayout: 'fixed',
  },
  loadingBlock: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
});

export type TableRootProps<Data> = {
  children: ({
    Row,
    Cell,
    EmptyData,
    SelectButton,
  }: {
    Row: RowType;
    Cell: CellType<Data>;
    EmptyData: EmptyDataType;
    SelectButton: SelectButtonType<Data>;
  }) => { props: { children: JSX.Element | JSX.Element[] } };
  data: Data[];
  id?: string;
  match?: (rowData: Data, search: string) => boolean;
  compare?: (a: Data, b: Data, field: string) => number;
  isSort?: Partial<Record<string, boolean>>;
  pagination?: number[] | boolean;
  tableLayoutFixed?: boolean;
  selectColumn?:
    | {
        hideColumn?: Array<Extract<keyof Data, string> | 'control' | 'controls' | 'operations'>;
        requiredColumns?: Array<
          Extract<keyof Data, string> | 'control' | 'controls' | 'operations'
        >;
      }
    | true;
  uniqueField?: Extract<keyof Data, string>;
  uniqueFieldGetter?: (obj: Data) => string | number;
  isLoading?: boolean | undefined;
  onChangeSort?: (sortDirection: { sortDir: number; sortCol: string }) => void;
  onChangeSearch?: (search: string) => void;
  create?: boolean | JSX.Element;
  onRowClick?: (row?: Data) => void;
  tableClassNames?: {
    rowClassName?: string;
  };
};

export function TableRoot<Data>({
  children,
  data,
  id,
  match,
  compare,
  isSort,
  pagination,
  tableLayoutFixed,
  selectColumn,
  uniqueField,
  uniqueFieldGetter,
  isLoading,
  onChangeSort,
  onChangeSearch,
  create,
  tableClassNames,
  onRowClick,
}: TableRootProps<Data>) {
  const classes = useStyles();
  const addClasses = useStylesTable();

  const [{ rowChildren }, emptyData, selectButton] = useChildren(
    children({
      Row,
      Cell,
      EmptyData,
      SelectButton,
    })
  );

  // Формируем список из столбцов что будут показываться пока пользователь не поменял что-либо.
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${id}.selectedColumns`, {
    ...rowChildren.reduce((pre, cell) => ({ ...pre, [cell.props.columnName]: true }), {}),
    ...(typeof selectColumn !== 'boolean' &&
      selectColumn?.hideColumn?.reduce((pre, cur) => ({ ...pre, [cur]: false }), {})),
    ...(typeof selectColumn !== 'boolean' &&
      selectColumn?.requiredColumns?.reduce((pre, cur) => ({ ...pre, [cur]: true }), {})),
  });

  const [sortDirection, setSortDirection] = useLocalStorage(`${id}.sortDirection`, {
    sortDir: 1,
    sortCol: isSort ? Object.keys(isSort)[0] : rowChildren[0].props.columnName,
  });
  const [currentPageSize, setCurrentPageSize] = useLocalStorage(
    `${id}.currentPageSize`,
    Array.isArray(pagination) ? pagination[0] : 10
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState<null | string>(null);
  const selectedColumnsWithNewRequiredColumns: Record<string, any> = {
    ...selectedColumns,
    ...(typeof selectColumn !== 'boolean' &&
      selectColumn?.requiredColumns?.reduce((pre, cur) => ({ ...pre, [cur]: true }), {})),
  };
  const rowChildrenShow = rowChildren.filter(
    (cell) => selectedColumnsWithNewRequiredColumns[cell.props.columnName]
  );

  let processedData = data;

  if (typeof match === 'function' && search) {
    processedData = processedData.filter((row) => match(row, search.toLowerCase()));
  }

  if (typeof isSort === 'object' && isSort[sortDirection.sortCol] && !onChangeSort) {
    processedData = processedData.sort(
      (a, b) => sortDirection.sortDir * (compare?.(a, b, sortDirection.sortCol) ?? 0)
    );
  }

  const hasPagination = pagination !== undefined;
  const countElementsBeforePagination = processedData.length;

  if (hasPagination) {
    processedData = processedData.slice(
      currentPage * currentPageSize,
      (currentPage + 1) * currentPageSize
    );
  }

  const [selected, setSelected] = useState<any[]>([]);

  useEffect(() => {
    setSelected([]);
  }, [data]);

  // В первый рендер передает родительскому компоненту значение из localstorage или дефолтное значение, если значения в localstorage еще нет.
  useEffect(() => {
    onChangeSort?.(sortDirection);
  }, [sortDirection]);

  // Если пользователь удаляет все данные на последней странице, то откинуть его на предыдущую страницу
  useEffect(() => {
    // Если пользователь удалил все данные на первой странице, то откидывать не нужно
    if (processedData.length === 0 && currentPage !== 0) {
      setCurrentPage((oldPage) => oldPage - 1);
    }
  }, [processedData]);

  const handleClick = useCallback(
    (event: any, field: any) =>
      event.target.checked
        ? setSelected((oldSelect) => [...oldSelect, field])
        : setSelected((oldSelect) => oldSelect.filter((item) => !Object.is(item, field))),
    [setSelected]
  );

  const handleSelectAllClick = useCallback(
    (event: any) => (event.target.checked ? setSelected(processedData) : setSelected([])),
    [processedData, setSelected]
  );

  const hasData = processedData.length > 0;

  return (
    <Paper className={`${classes.root} ${addClasses.paper}`} elevation={0}>
      {selected.length > 0 ? (
        <TableToolbarSelectRow selectButton={selectButton} selectedElements={selected} />
      ) : (
        (match !== undefined ||
          selectColumn !== undefined ||
          create !== undefined ||
          hasPagination) && (
          <TableToolbar
            searchEnabled={match !== undefined}
            create={create}
            search={search}
            setSearch={(searchValue) => {
              setSearch(searchValue);
              onChangeSearch?.(searchValue || '');
            }}
            hasPagination={hasPagination}
            pagination={pagination}
            setCurrentPage={setCurrentPage}
            setCurrentPageSize={setCurrentPageSize}
            selectColumn={selectColumn}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            countElementsBeforePagination={countElementsBeforePagination}
            processedDataLength={processedData.length}
            currentPageSize={currentPageSize}
            currentPage={currentPage}
            columnTitles={rowChildren.reduce(
              (pre, cell) => ({
                ...pre,
                [cell.props.columnName]: cell.props.headtitle,
              }),
              {}
            )}
          />
        )
      )}
      {hasData ? (
        <div className={classes.tableHoC}>
          <Table
            className={`${tableLayoutFixed ? classes.tableLayoutFixed : ''} ${addClasses.table}`}
            size="small"
          >
            <TableHeadRoot
              addClasses={{
                root: addClasses.header,
                cell: addClasses.headerCell,
              }}
              rowChildrenShow={rowChildrenShow}
              isSort={isSort}
              sortDirection={sortDirection}
              setSortDirection={(updateSortDirectionFunction) =>
                setSortDirection((sortDir) => updateSortDirectionFunction(sortDir))
              }
              selectButton={selectButton}
              processedDataLength={processedData.length}
              selectedLength={selected.length}
              changeAll={handleSelectAllClick}
            />
            <TableBody>
              {isLoading && (
                <div className={classes.loadingBlock}>
                  <Loading />
                </div>
              )}
              {processedData.map((rowData, index) => (
                <TableRow
                  className={
                    tableClassNames === undefined ? undefined : tableClassNames.rowClassName
                  }
                  onClick={() => (onRowClick === undefined ? undefined : onRowClick(rowData))}
                  key={
                    uniqueFieldGetter
                      ? uniqueFieldGetter(rowData)
                      : uniqueField
                      ? (rowData[uniqueField] as any)
                      : index
                  }
                >
                  <RowProvider row={rowData}>
                    <>
                      {selectButton && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={Boolean(selected.find((item) => Object.is(item, rowData)))}
                            onChange={(event) => handleClick(event, rowData)}
                          />
                        </TableCell>
                      )}
                      {rowChildrenShow}
                    </>
                  </RowProvider>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        emptyData
      )}
    </Paper>
  );
}
